import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { authApi } from '@/api/auth';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import { Eyebrow, Display } from '@/components/ui/Typography';
import { forgotPasswordSchema, type ForgotPasswordFormData } from '@/lib/validation';
import { friendlyMessage } from '@/lib/errors';
import { useKeyboardViewport } from '@/hooks/useKeyboardViewport';

const SUPPORT_URL =
  import.meta.env.VITE_SUPPORT_WHATSAPP_URL ?? 'https://wa.me/5527988231783';

export default function ForgotPassword() {
  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
  } = useForm<ForgotPasswordFormData>({ resolver: zodResolver(forgotPasswordSchema) });

  const navigate = useNavigate();
  const [sent, setSent] = useState(false);
  const [apiError, setApiError] = useState<string | null>(null);
  const { kbOpen, vvHeight } = useKeyboardViewport();

  async function onSubmit(data: ForgotPasswordFormData) {
    setApiError(null);
    try {
      await authApi.forgotPassword(data.email);
      setSent(true);
    } catch (err) {
      setApiError(friendlyMessage(err, 'Não foi possível enviar o link. Tente novamente.'));
    }
  }

  // NSS (design/no-scroll/nss-auth.jsx · ForgotResetNS): tela curta —
  // viewport fixa, espaçador absorve a folga, CTA ancorado via espaçador.
  return (
    <div
      className="mx-auto flex max-w-sm flex-col overflow-hidden px-7"
      style={{
        height: kbOpen && vvHeight !== null ? `${vvHeight}px` : '100dvh',
        paddingTop: kbOpen ? 10 : 'max(2rem, env(safe-area-inset-top))',
        paddingBottom: kbOpen ? 10 : 'max(1.75rem, env(safe-area-inset-bottom))'
      }}
    >
      <div
        className={`flex flex-shrink-0 items-center ${
          kbOpen ? 'mb-3' : 'mb-8 [@media(max-height:740px)]:mb-5'
        }`}
      >
        <img
          src="/logo/logo-bow-01.png"
          alt="Bow"
          className={kbOpen ? 'h-7 w-auto' : 'h-9 w-auto'}
        />
      </div>

      <div className="flex min-h-0 flex-1 flex-col overflow-y-auto">
      <Eyebrow>Recuperar acesso</Eyebrow>
      <Display
        size={36}
        className={`mt-3 ${
          kbOpen
            ? '!text-[24px] !leading-tight mt-1'
            : '[@media(max-height:740px)]:!text-[28px]'
        }`}
      >
        Esqueceu sua
        <br />
        <span className="text-bow-green">senha?</span>
      </Display>

      {sent ? (
        <>
        <div className="min-h-4 flex-1" />
        <div className="flex flex-col gap-5">
          <div className="rounded-3xl bg-bow-green/10 p-5">
            <p className="text-sm font-semibold text-bow-navy">Email enviado</p>
            <p className="mt-1 text-sm leading-relaxed text-bow-navy/70">
              Se o email estiver cadastrado, você receberá um link para redefinir sua
              senha em breve. Verifique também sua caixa de spam.
            </p>
          </div>

          <p className="text-center text-[13px] text-bow-royal/55">
            Não chegou?{' '}
            <a
              href={SUPPORT_URL}
              target="_blank"
              rel="noopener noreferrer"
              className="font-semibold text-bow-royal"
            >
              Fale com o suporte
            </a>
          </p>

          <Button variant="secondary" onClick={() => navigate('/login')}>
            Voltar ao login
          </Button>
        </div>
        </>
      ) : (
        /* Campos logo abaixo do hero — input visível no focus = iOS não pana
           a página (mesmo fix do passo de e-mail do Login, 2026-07-04).
           Espaçador dentro do form: CTA no rodapé / acima do teclado. */
        <div className="flex min-h-0 flex-1 flex-col gap-4 pt-5">
          <p className="text-sm leading-relaxed text-bow-royal/60">
            Digite seu email de cadastro e enviaremos um link para redefinir sua senha.
          </p>

          <form
            onSubmit={handleSubmit(onSubmit)}
            className="flex min-h-0 flex-1 flex-col gap-3"
          >
            <Input
              label="E-mail"
              type="email"
              autoComplete="email"
              placeholder="ana@email.com"
              error={errors.email?.message}
              {...register('email')}
            />
            {apiError && <p className="px-1 text-sm text-bow-red">{apiError}</p>}
            <div className="min-h-2 flex-1" />
            <Button
              type="submit"
              disabled={isSubmitting}
              onPointerDown={(e) => e.preventDefault()}
            >
              {isSubmitting ? 'Enviando…' : 'Enviar link'}
            </Button>
          </form>

          <p
            className={`text-center text-[13px] text-bow-royal/55 ${
              kbOpen ? 'hidden' : ''
            }`}
          >
            Lembrou a senha?{' '}
            <Link to="/login" className="font-semibold text-bow-royal">
              Fazer login
            </Link>
          </p>
        </div>
      )}
      </div>
    </div>
  );
}
